import React, { useEffect, useMemo } from 'react';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	HandleActionType,
	type ConversationAction,
} from '@atlassian/rovo-conversation-actions/types';

const searchAssistanceGreetingTitle = defineMessage({
	id: 'ai-rovo.actions-route-provider.jira-rovo-search.assistance-greeting-title',
	defaultMessage: 'Can I help you find something{name, select, undefined {} other {, {name}}}?',
	description: 'Title of the search assistance greeting.',
});

const searchAssistanceNudgeSubheaderLabel = defineMessage({
	id: 'ai-rovo.actions-route-provider.jira-rovo-search.assistance-nudge-subheader',
	defaultMessage: 'Search across your work to find what you need',
	description: 'Label for the search assistance nudge subheader',
});

export const AiRovoJiraRovoSearchNudgesProvider: React.FC<{}> = (): null => {
	const publishRovo = usePublish('ai-mate');
	const { formatMessage } = useIntl();
	const { data: currentUser } = useCurrentUser();
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();

	// Get user display name, preferring displayName over userFullname
	const user = currentUser?.user;
	const fullDisplayName =
		(user && 'displayName' in user ? user.displayName : null) || user?.userFullname || 'there';

	// Extract first name from display name (split by space and take first part)
	const firstName = useMemo(
		() => (fullDisplayName === 'there' ? 'there' : fullDisplayName.split(' ')[0]),
		[fullDisplayName],
	);

	const title = formatMessage(searchAssistanceGreetingTitle, {
		name: firstName || undefined,
	});

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const baseQueryStartersNudge: ConversationAction = {
				id: 'nudge-to-query-starters-ai-action',
				title,
				secondaryTitle: formatMessage(searchAssistanceNudgeSubheaderLabel),
				icon: () => <AiSearchIcon label="" color="currentColor" />,
				handleType: HandleActionType.Base,
				type: 'ai-action',
				nudgeContext: {
					id: 'nudge-to-query-starters',
				},
				onClick: () => {
					publishRovo({
						type: 'chat-new',
						source: 'advanced-search-jira',
						openChat: true,
						openChatMode: 'mini-modal',
						data: { dialogues: [] },
					});
				},
			};

			updateConfiguration({
				nudges: [{ ...baseQueryStartersNudge }],
			});
		}, 20000);
		return () => clearTimeout(timeoutId);
	}, [updateConfiguration, publishRovo, title, formatMessage]);

	return null;
};
